import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';

import AudienceService from '../../services/AudienceService';
import Authorities from '../../services/AuthoritiesService';
import ChangePasswordForm from '../auth/ChangePasswordForm';
import clienteAxios from '../../config/axios';
import { collapseMenuAction, expandMenuAction } from '../../actions/systemActions';
import { ErrorMessage, WaitingMessage } from '../extras/toastMessages';
import { getBuildDate } from '../../helpers/utils';
import { getUserDataAction, logoutAction } from '../../actions/authActions';
import { ICON_BELL, ICON_CALENDAR_DAY, ICON_NEW, ICON_SHORTCUT } from '../extras/icons';
import { listWorkListAction } from '../../actions/audiencesActions';
import { MSG_ERROR_SERVER, MSG_SEARCHING_AUDIENCE } from '../../constants/messages';
import packageJson from '../../../package.json';
import ProfileForm from '../auth/ProfileForm';
import { PROFILE_OGA, PROFILE_SISTEMAS } from '../../data/profiles';
import { SOLICITUDES_CREAR, SOLICITUDES_MODIFICAR } from '../../data/objects';
import { useCurrentCenter } from '../../hooks/useCurrentCenter';


const TopBar = () => {

  // =====================================================================
  // DECLARACIONES -------------------------------------------------------
  // =====================================================================

  // Traigo el history
  const history = useHistory();

  // Inicializo el dispatch
  const dispatch = useDispatch();

  // Traigo el store
  const audiences = useSelector( state => state.audiences.audiencesWorkList );
  const authorities = useSelector(state => state.auth.authorities);
  const authUser = useSelector( state => state.auth.user );
  const expandMenu = useSelector( state => state.system.expandMenu );

  // Inicializo el state local
  const [preventCancelChangePassword, setPreventCancelChangePassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [text, setText] = useState('');

  // Inicializo 
  const center = useCurrentCenter();
  const claseMenu = `navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow ${center?.clase_contenedor || 'div-default'}`;
  const today = moment().format('YYYY-MM-DD');


  // =====================================================================
  // FUNCIONES -----------------------------------------------------------
  // =====================================================================

  // Manejo los cambios
  const handleChange = e => {
    setText(e.target.value);
  }
  
  // Manejo el cierre de la ventana de pass
  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
    setPreventCancelChangePassword(false);
  }

  // Manejo cierre
  const handleCloseProfile = () => {
    setShowProfile(false);
  }
  
  // Manejo el botón de expandir
  const handleExpand = () => {
    if(expandMenu)
    { dispatch( collapseMenuAction() );
    }
    else
    { dispatch( expandMenuAction() );
    }
  }
  
  // Manejo la apertura del cambio de clave del primer login
  const handleFirstLogin = () => {
    setPreventCancelChangePassword(true);
    setShowChangePassword(true);
  }

  // Manejo el logout
  const handleLogout = e => {
    e.preventDefault();
    dispatch( logoutAction(history) );
  }

  //Manejo la búsqueda
  const handleSearch = async () => {
    //Me fijo que sea un número
    if(isNaN(text) || typeof text === "undefined" || text.trim() === '')
    { ErrorMessage('Debe indicar un ID de Audiencia');
      return false;
    }
    try
    { WaitingMessage(MSG_SEARCHING_AUDIENCE);
      const params = { search: `id:${text}` };
      let method = '/audiencia/dto';
      if(authUser.perfil.id === PROFILE_SISTEMAS || authUser.perfil.id === PROFILE_OGA)
      { method = '/audiencia';
      }
      const response = await clienteAxios.get(`${method}`, { params });
      const audiences = response.data.content;
      Swal.close();
      if(audiences.length > 0)
      { const audience = audiences[0];
        if(AudienceService.hasAccess(audience, authUser)) 
        { setText('');
          if(authorities && Authorities.hasPermission(authorities, SOLICITUDES_MODIFICAR)) 
          { history.push(`/audiencias/${audience.id}/formulario-trabajo/informacion`);
          }
          else
          { history.push(`/audiencias/${audience.id}/ver`);
          }
        }
        else
        { ErrorMessage('No se encontraron audiencias asociadas');  
        }        
      }
      else
      { ErrorMessage('No se encontraron audiencias asociadas');
      }
    }
    catch(error)
    { const response = error.response;
      let data = {
        data: null,
        message: MSG_ERROR_SERVER
      };
      if(response)
      { data = response.data;
      }
      Swal.close();
      ErrorMessage(data.message);
    }
  }

  // Manejo el submit
  const handleSubmit = e => {
    e.preventDefault();
    handleSearch();
  }

  // Cargo las audiencias al inicio
  useEffect(() => {
    if( (authUser?.perfil?.id === PROFILE_OGA || authUser?.perfil?.id === PROFILE_SISTEMAS) && (!audiences) )
    { dispatch( listWorkListAction() );
    }
    if(!authUser)
    { dispatch( getUserDataAction() );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Me fijo el cambio de usuario
  useEffect(() => {
    if(authUser && authUser.cc === 1)
    { handleFirstLogin();
    }
  }, [authUser]);


  // ====================================================================
  // RENDERIZADO --------------------------------------------------------
  // ====================================================================

  return (
      <nav className={claseMenu}>

        <button id="sidebarToggleTop" className="btn btn-link d-md-none rounded-circle mr-3" onClick={handleExpand}>
          <i className="fa fa-bars"></i>
        </button>

        <form 
          autoComplete="off"
          className="d-none d-sm-inline-block form-inline mr-auto ml-md-3 my-2 my-md-0 mw-100 navbar-search" 
          onSubmit={handleSubmit}
        >
          <div className="input-group">
            <input 
              aria-describedby="basic-addon2"
              aria-label="Buscar" 
              className="form-control bg-light border-0 small" 
              name="text"
              onChange={handleChange}
              placeholder="Buscar audiencia por ID..." 
              type="text" 
              value={text}
            />
            <div className="input-group-append">
              <button className="btn btn-primary" type="submit">
                <i className="fas fa-search fa-sm"></i>
              </button>
            </div>
          </div>
        </form>

        <ul className="navbar-nav ml-auto">

          <li className="nav-item dropdown no-arrow d-sm-none">
            <button
              aria-expanded="false"
              aria-haspopup="true"
              className="nav-link dropdown-toggle btn btn-link p-0"
              data-toggle="dropdown"
              id="searchDropdown"
              style={{ 
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                margin: 0,
                padding: 0,
                textDecoration: 'none',
                outline: 'none',
                boxShadow: 'none'
              }}
              type="button"
            >
              <i className="fas fa-search fa-fw"></i>
            </button>
            <div className="dropdown-menu dropdown-menu-right p-3 shadow animated--grow-in" aria-labelledby="searchDropdown">
              <form 
                autoComplete="off"
                className="form-inline mr-auto w-100 navbar-search"
                onSubmit={handleSubmit}
              >
                <div className="input-group mb-2">
                  <input 
                    aria-describedby="basic-addon2"
                    aria-label="Buscar" 
                    className="form-control bg-light border-0 small" 
                    name="text"
                    onChange={handleChange}
                    placeholder="Buscar audiencia por ID..." 
                    type="text"  
                    value={text}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-primary" type="submit">
                      <i className="fas fa-search fa-sm"></i>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </li>

          <li className="nav-item no-arrow mx-1">
              <span className="nav-link text-warning">
                <small>v{ getBuildDate(packageJson.buildDate) }</small>
              </span>
          </li>

          <li className="nav-item dropdown no-arrow mx-1">
            <div className="btn-centro">
              { center?.shortname || 'OGA' }
            </div>
          </li>

          { authorities ? (
              <li className="nav-item dropdown no-arrow mx-1">
                <button
                  aria-expanded="false"
                  aria-haspopup="true"
                  className="btn btn-link nav-link dropdown-toggle text-white p-0"
                  data-toggle="dropdown"
                  id="actionsDropdown"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    margin: 0,
                    padding: 0,
                    textDecoration: 'none',
                    outline: 'none',
                    boxShadow: 'none'
                  }}
                  type="button"
                >
                  <ICON_SHORTCUT className="fa-fw" />
                </button>
                
                <div className="dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in" aria-labelledby="actionsDropdown">
                  <h6 className="dropdown-header">
                    Accesos directos
                  </h6>

                  { Authorities.hasPermission(authorities, SOLICITUDES_CREAR) ? (
                      <Link className="dropdown-item d-flex align-items-center" to="/audiencias/nueva-solicitud">
                        <div className="mr-3">
                          <div className="icon-circle bg-primary">
                            <ICON_NEW className="text-white" />
                          </div>
                        </div>
                        <div>
                          <div className="small text-gray-500">Audiencias</div>
                          <span className="font-weight-bold">NUEVA SOLICITUD</span>
                        </div>
                      </Link>
                    ) : null
                  }

                  <Link className="dropdown-item d-flex align-items-center" to={`/audiencias/${today}/dia`} target="_blank">
                    <div className="mr-3">
                      <div className="icon-circle bg-primary">
                        <ICON_CALENDAR_DAY className="text-white" />
                      </div>
                    </div>
                    <div>
                      <div className="small text-gray-500">Audiencias</div>
                      <span className="font-weight-bold">LISTADO DEL DÍA</span>
                    </div>
                  </Link>
                  
                  <Link className="dropdown-item text-center small text-gray-500" to="/audiencias">
                    Ver Todas las Audiencias
                  </Link>
                </div>
              </li>
            ) : null
          }
          
          { authUser && (authUser.perfil.id === PROFILE_SISTEMAS || authUser.perfil.id === PROFILE_OGA) ? (
              <li className="nav-item dropdown no-arrow mx-1">
                <button
                  aria-expanded="false"
                  aria-haspopup="true"
                  className="btn btn-link nav-link dropdown-toggle text-white p-0"
                  data-toggle="dropdown"
                  id="alertsDropdown"
                  style={{
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    cursor: 'pointer',
                    height: '21px',
                    marginTop: '25px',
                    outline: 'none',
                    padding: 0,
                    textDecoration: 'none',
                  }}
                  type="button"
                >
                  <ICON_BELL className="fa-fw" />
                  <span className="badge badge-danger badge-counter" style={{right:'-.2rem', marginTop:'0rem'}}>
                    { audiences && audiences.length }
                  </span>
                </button>
                
                { audiences ? (
                    <div className="dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in" aria-labelledby="alertsDropdown">
                      <h6 className="dropdown-header">
                        Solicitudes
                      </h6>
                      { audiences.slice(0, 3).map((a, index) => (
                          <Link 
                            className="dropdown-item d-flex align-items-center" 
                            key={`faa${a.id}`}
                            to={`/audiencias/${a.id}/formulario-trabajo/informacion`}
                          >
                            <div className="mr-3">
                              <div className="icon-circle bg-primary">
                                <i className="fas fa-file-alt text-white"></i>
                              </div>
                            </div>
                            <div>
                              <div className="small text-gray-500">{ a.created_at ? moment(a.created_at).format('DD/MM/YYYY HH:mm') : '' }</div>
                              <span className="font-weight-bold">
                                { a.file_number || a.case_title }
                                <br/>
                                <small>
                                { a.audience_types?.map((type, index) => (
                                    <React.Fragment key={`faaat${a.id}${index}`}>
                                      {`${index > 0 ? '|' : ''}${type}`}
                                    </React.Fragment>
                                  ))
                                }
                                </small>
                              </span>
                            </div>
                          </Link>
                        ))
                      }
                      <Link className="dropdown-item text-center small text-gray-500" to="/">
                        Mostrar todas las Solicitudes
                      </Link>
                    </div>
                  ) : null
                }
              </li>
            ) : null
          }          

          <div className="topbar-divider d-none d-sm-block"></div>

          <li className="nav-item dropdown no-arrow">
            <button
              aria-expanded="false"
              aria-haspopup="true"
              className="btn btn-link nav-link dropdown-toggle p-0"
              data-toggle="dropdown"
              id="userDropdown"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                margin: 0,
                padding: 0,
                textDecoration: 'none',
                outline: 'none',
                boxShadow: 'none'
              }}
              type="button"
            >
              <span className="mr-2 d-none d-lg-inline text-white small">
                { authUser ? `${authUser.nombre.substring(0, 1)}. ${authUser.apellido}` : 'Usuario' }
              </span>
              <img alt="user-avatar" className="img-profile rounded-circle" src="/img/user-default.png" />
            </button>
            
            <div className="dropdown-menu dropdown-menu-right shadow animated--grow-in" aria-labelledby="userDropdown">
              <Button className="dropdown-item" onClick={() => setShowProfile(true)}>
                <i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400"></i>
                Perfil
              </Button>

              <ProfileForm
                handleClose={handleCloseProfile}
                show={showProfile}
                user={authUser}
              />

              <Button className="dropdown-item" onClick={() => setShowChangePassword(true)}>
                <i className="fas fa-key fa-sm fa-fw mr-2 text-gray-400"></i>
                Contraseña
              </Button>

              <ChangePasswordForm
                handleClose={handleCloseChangePassword}
                preventCancel={preventCancelChangePassword}
                show={showChangePassword}
              />
              
              <div className="dropdown-divider"></div>
              <a className="dropdown-item" href="!#" data-toggle="modal" data-target="#logoutModal" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i>
                Salir
              </a>
            </div>
          </li>

        </ul>
      </nav>
  );
};

export default TopBar;